import { useState, useEffect } from "react";
import { ListChecks, Move3D, Grid3x3, Users2 } from "lucide-react";

const STEPS = [
  {
    icon: ListChecks,
    title: "1. Crea tu workspace y board",
    desc: "Define tu proyecto en segundos: nómbralo, elige un color y agrega las listas que representen tus etapas (Backlog → En progreso → Hecho).",
  },
  {
    icon: Move3D,
    title: "2. Convierte ideas en tarjetas",
    desc: "Cada tarjeta es una tarea con descripción, fecha límite, etiquetas, checklists y comentarios. Arrástrala entre listas para reflejar su avance.",
  },
  {
    icon: Grid3x3,
    title: "3. Prioriza con la matriz",
    desc: "Alterna a la vista de matriz Urgente/Importante y descubre rápidamente qué resolver primero. La misma data, otra perspectiva.",
  },
  {
    icon: Users2,
    title: "4. Suma a tu equipo",
    desc: "Invita por email o comparte un link. Todos ven los cambios en vivo, comentan con Markdown y mantienen el historial limpio.",
  },
];

export default function WorkflowSection() {
  const [active, setActive] = useState(0);

  // Auto-advance every 3.5s; pauses when the user interacts.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive((a) => (a + 1) % STEPS.length), 3500);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section id="how" className="relative py-20 md:py-28 bg-gradient-to-b from-transparent via-blue-500/[0.03] to-transparent" data-testid="landing-workflow">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-block text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400">
            Cómo funciona
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-2 leading-[1.05]">
            De la idea al resultado, en 4 pasos.
          </h2>
          <p className="text-muted-foreground mt-4 text-base md:text-lg">
            Sprintroll está pensado para empezar a operar el mismo día. Cero configuración pesada.
          </p>

          <div
            className="mt-10 space-y-2"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            {STEPS.map(({ icon: Icon, title, desc }, i) => {
              const isActive = i === active;
              return (
                <button
                  key={i}
                  data-testid={`workflow-step-${i}`}
                  onClick={() => setActive(i)}
                  className={
                    "w-full text-left rounded-2xl p-4 md:p-5 border transition flex gap-4 group " +
                    (isActive
                      ? "bg-white/70 dark:bg-slate-900/70 border-blue-500/40 shadow-lg shadow-blue-500/10"
                      : "border-transparent hover:bg-white/40 dark:hover:bg-white/5")
                  }
                >
                  <div className={
                    "h-10 w-10 flex-none rounded-xl grid place-items-center transition " +
                    (isActive
                      ? "bg-blue-500 text-white shadow-md shadow-blue-500/40"
                      : "bg-blue-500/10 text-blue-600 dark:text-blue-300")
                  }>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className={"font-display font-semibold text-base " + (isActive ? "" : "text-foreground/80")}>{title}</p>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Visual side: animated illustration per step */}
        <div className="relative">
          <div className="glass-panel rounded-3xl p-6 md:p-8 shadow-2xl shadow-blue-500/10 ring-1 ring-blue-500/10 min-h-[320px] md:min-h-[420px] flex items-center justify-center overflow-hidden">
            <StepVisual step={active} />
          </div>
          {/* Step pagination dots */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`Paso ${i + 1}`}
                data-testid={`workflow-dot-${i}`}
                className={
                  "h-2 rounded-full transition-all " +
                  (i === active ? "w-8 bg-blue-500" : "w-2 bg-blue-500/30 hover:bg-blue-500/50")
                }
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StepVisual({ step }) {
  // Each visual re-mounts with key to retrigger CSS animations.
  return (
    <div key={step} className="w-full animate-fade-rise">
      {step === 0 && <CreateWorkspaceVisual />}
      {step === 1 && <CardsVisual />}
      {step === 2 && <MatrixVisual />}
      {step === 3 && <TeamVisual />}
    </div>
  );
}

function CreateWorkspaceVisual() {
  return (
    <div className="space-y-3 max-w-sm mx-auto w-full">
      <div className="rounded-2xl border border-blue-500/40 bg-white dark:bg-slate-800 p-4 shadow-md">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Nombre del workspace</div>
        <div className="mt-1 text-lg font-display font-semibold text-foreground">Producto 2026</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {["Backlog", "Por hacer", "En progreso", "Hecho"].map((n, i) => (
          <div key={n}
               className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-white/10 p-3 shadow-sm animate-fade-rise"
               style={{ animationDelay: `${i * 100}ms` }}>
            <div className="h-1.5 w-8 rounded-full bg-blue-500 mb-2" />
            <div className="text-xs font-semibold text-foreground">{n}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CardsVisual() {
  const cards = [
    { t: "Redactar landing page", cls: "bg-violet-500/15 text-violet-700 dark:text-violet-300" },
    { t: "Integrar pagos PayPal", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
    { t: "Pruebas en mobile", cls: "bg-sky-500/15 text-sky-700 dark:text-sky-300" },
  ];
  return (
    <div className="space-y-2.5 max-w-xs mx-auto w-full">
      {cards.map((card, i) => (
        <div
          key={card.t}
          className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-slate-200/80 dark:border-white/10 shadow-md animate-fade-rise"
          style={{ animationDelay: `${i * 120}ms` }}
        >
          <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full ${card.cls}`}>
            Etiqueta
          </span>
          <div className="text-sm font-semibold text-foreground mt-1.5">{card.t}</div>
          <div className="mt-2 h-1 w-full rounded-full bg-blue-500/15 overflow-hidden">
            <div className="h-full bg-blue-500" style={{ width: `${40 + i * 20}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function MatrixVisual() {
  const cells = [
    { label: "Urgente · Importante",       cls: "bg-rose-500/15 border-rose-500/40",      n: 3 },
    { label: "No urgente · Importante",    cls: "bg-amber-500/15 border-amber-500/40",    n: 5 },
    { label: "Urgente · No importante",    cls: "bg-sky-500/15 border-sky-500/40",        n: 2 },
    { label: "No urgente · No importante", cls: "bg-slate-500/15 border-slate-500/40",    n: 4 },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 max-w-md mx-auto w-full">
      {cells.map((c, i) => (
        <div
          key={c.label}
          className={`rounded-2xl p-4 border-2 animate-fade-rise ${c.cls}`}
          style={{ animationDelay: `${i * 90}ms` }}
        >
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">{c.label}</div>
          <div className="text-3xl font-display font-semibold mt-1 text-foreground">{c.n}</div>
          <div className="text-[10px] text-muted-foreground">tareas</div>
        </div>
      ))}
    </div>
  );
}

function TeamVisual() {
  const team = [
    { n: "AL", c: "bg-blue-500" },
    { n: "MR", c: "bg-emerald-500" },
    { n: "JS", c: "bg-violet-500" },
    { n: "+5", c: "bg-slate-700" },
  ];
  return (
    <div className="max-w-sm mx-auto w-full space-y-4">
      <div className="flex items-center gap-2 justify-center">
        {team.map((m, i) => (
          <div
            key={m.n}
            className={`h-12 w-12 rounded-full ${m.c} text-white grid place-items-center font-semibold animate-fade-rise shadow-lg ring-2 ring-white dark:ring-slate-900`}
            style={{ animationDelay: `${i * 110}ms` }}
          >
            {m.n}
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-white/10 p-4 shadow-md">
        <div className="text-xs text-muted-foreground">Marta movió</div>
        <div className="text-sm font-semibold mt-0.5 text-foreground">&quot;Diseño onboarding&quot; a En progreso</div>
        <div className="text-[10px] text-muted-foreground mt-1">hace 2 min</div>
      </div>
    </div>
  );
}
